import React, { useState } from 'react';
import { format } from 'date-fns';
import {
  Mail, MailOpen, Check, Ban, ShieldAlert, Paperclip, Loader2, CornerUpLeft, User,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import CustomerEmailComposer from '@/components/admin/crm/CustomerEmailComposer';

const STATUS_ACTIONS = [
  { key: 'handled', label: 'HANDLED', icon: Check },
  { key: 'dismissed', label: 'DISMISSED', icon: Ban },
  { key: 'spam', label: 'SPAM', icon: ShieldAlert },
];

// The full exchange a reply belongs to, plus the triage controls and an inline
// reply composer that reuses the CRM composer so branding and invoice
// attachment behave identically.
export default function EmailThreadPanel({ message, messages = [], onChanged }) {
  const [busy, setBusy] = useState('');
  const [composing, setComposing] = useState(false);

  const setStatus = async (status) => {
    setBusy(status);
    try {
      await base44.entities.CustomerEmailLog.updateMany(
        { thread_id: message.thread_id || message.id, direction: 'inbound' },
        { $set: { inbox_status: status } }
      );
      if (onChanged) onChanged();
    } catch (e) {
      console.error('Failed to update reply status:', e);
    }
    setBusy('');
  };

  const customerEmail = message.customer_email || message.from_address;
  const firstName = (message.customer_name || '').trim().split(/\s+/)[0] || '';
  const replySubject = (message.subject || '').replace(/^((re|fwd|fw)\s*:\s*)+/i, '');

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-white font-bold text-sm truncate">
            {message.customer_name || message.from_address}
          </p>
          <p className="text-white/30 text-xs truncate">{customerEmail}</p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {STATUS_ACTIONS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setStatus(key)}
              disabled={!!busy}
              className={`inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest px-2.5 py-1.5 border transition-colors disabled:opacity-50 ${
                message.inbox_status === key
                  ? 'border-primary text-primary bg-primary/10'
                  : 'border-white/10 text-white/50 hover:text-white hover:border-primary/50'
              }`}
            >
              {busy === key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Conversation context — the email we sent, then every reply */}
      <div className="space-y-2">
        {messages.map((m) => {
          const inbound = m.direction === 'inbound';
          return (
            <div
              key={m.id}
              className={`border bg-[#0d0d0f] ${
                inbound ? 'border-l-2 border-l-primary border-y-white/5 border-r-white/5' : 'border-white/5'
              }`}
            >
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5">
                {inbound ? (
                  <MailOpen className="w-3.5 h-3.5 text-primary shrink-0" />
                ) : (
                  <Mail className="w-3.5 h-3.5 text-white/40 shrink-0" />
                )}
                <p className="text-white/70 text-xs font-bold truncate flex-1">
                  {inbound
                    ? `REPLY FROM ${(m.from_address || m.customer_email || '').toUpperCase()}`
                    : `SENT BY ${(m.admin_name || m.admin_email || 'ADMIN').toUpperCase()}`}
                </p>
                {m.auto_generated && (
                  <span className="text-white/25 text-[9px] font-bold tracking-widest">AUTO</span>
                )}
                <p className="text-white/25 text-[10px] shrink-0">
                  {format(new Date(m.created_date), 'dd MMM yyyy · HH:mm')}
                </p>
              </div>
              <div className="px-4 py-3">
                <p className="text-white/80 text-xs font-bold mb-2">{m.subject || '(no subject)'}</p>
                <p className="text-white/60 text-xs leading-relaxed whitespace-pre-wrap">
                  {m.body || '(no message body)'}
                </p>
                {(m.attachment_names || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {m.attachment_names.map((n) => (
                      <span
                        key={n}
                        className="inline-flex items-center gap-1.5 border border-white/10 text-white/40 text-[10px] px-2 py-1"
                      >
                        <Paperclip className="w-3 h-3 text-primary" /> {n}
                      </span>
                    ))}
                  </div>
                )}
                {m.attachment_filename && (
                  <div className="mt-3 inline-flex items-center gap-1.5 border border-white/10 text-white/40 text-[10px] px-2 py-1">
                    <Paperclip className="w-3 h-3 text-primary" /> {m.attachment_filename}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {composing ? (
        <CustomerEmailComposer
          customerEmail={customerEmail}
          customerName={message.customer_name}
          defaultSubject={`Re: ${replySubject}`}
          defaultBody={`Hi ${firstName || 'there'},\n\n`}
          onClose={() => setComposing(false)}
          onSent={() => { setComposing(false); if (onChanged) onChanged(); }}
        />
      ) : (
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setComposing(true)}
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-4 py-2.5 transition-colors"
          >
            <CornerUpLeft className="w-4 h-4" /> REPLY TO CUSTOMER
          </button>
          <Link
            to="/admin/crm/directory"
            className="inline-flex items-center gap-1.5 text-white/40 hover:text-white text-[10px] font-bold tracking-widest border border-white/10 px-3 py-2 transition-colors"
          >
            <User className="w-3 h-3" /> OPEN CUSTOMER RECORD
          </Link>
        </div>
      )}
    </div>
  );
}